import type { KnowledgeUnit } from '../../types'
import type { TenantOverlay } from '../index'

// ---------------------------------------------------------------------------
// tenants/greencrate.ts — GreenCrate tenant overlay corpus
//
// The advisor runs on the greencrate.aleph.econ.works synthetic tenant; these
// units teach it GreenCrate's vocabulary (box product lines, subscription
// funnel, media mix, named campaigns) and the five scripted findings the demo
// dossier walks a prospect through. Appended to CORE_UNITS by the tenant overlay
// seam (knowledge/index.ts) when VITE_TENANT_KEY selects greencrate.
//
// Sources of truth (do not drift from these):
//   - Labels = what the generator emits (db/synthetic/src/emitters/metadata.ts +
//     world.ts), which is what get_controls serves. Where the dossier disagreed
//     with the generator, the generator wins (funnel labels: New Subscriptions /
//     Net Sales / First Reorder — NOT Gross Adds / 1st Top-Up).
//   - Finding playbooks (type 'finding') = page + control settings + qualitative
//     expectation only. Per the anti-hallucination contract (systemText.ts) NO
//     specific figures appear here — query_kpi supplies live numbers at answer time.
//
// Draft revision v0 (not yet analyst-greenlit; matches the core corpus cadence).
// ---------------------------------------------------------------------------

export const GREENCRATE_UNITS: KnowledgeUnit[] = [
  // ─── Brand context ────────────────────────────────────────────────────────
  {
    id: 'concept:greencrate-brand',
    type: 'concept',
    keys: [
      'greencrate',
      'green crate',
      'meal-kit',
      'meal kit',
      'subscription box',
      'brand',
      'tenant',
      'company',
      'what is greencrate',
      'dataset',
      'synthetic',
    ],
    version: '0.1-draft',
    title: 'GreenCrate (brand context)',
    summary:
      'GreenCrate is the synthetic meal-kit subscription brand this tenant demonstrates; daily data spans early 2023 through mid-2026.',
    body: `GreenCrate is a fictional, enterprise-scale meal-kit subscription brand. It exists purely as a whitelabel demo tenant: a full-sized, realistic dataset showing what the platform looks like once a real analyst's data is loaded, without touching any customer's actual numbers. All of it is synthetic — generated from a parameterized model — so every figure is safe to show to anyone.

What the data covers: daily observations across the model window 2023-01-01 → 2026-06-30 (the anchor date), for a single brand (GreenCrate) sold as box product lines through several purchase channels, supported by a nine-medium marketing mix and a roster of named campaigns.

Register: speak in GreenCrate's own product and media language — boxes and subscriptions, purchase channels, mediums, and campaigns — not generic MMM jargon. The dossier defines the vocabulary; the other GreenCrate knowledge units carry the specifics.

Grounding: you have no pre-loaded GreenCrate figures. Any number you state must come from a live query_kpi result — never quote a spend, RoI, or share from memory or from the dossier.`,
    seeAlso: [
      'concept:greencrate-value-types',
      'concept:greencrate-media-driven-sales',
      'dim:greencrate-medium-level-1',
    ],
  },

  // ─── KPI / value-type vocabulary ────────────────────────────────────────────
  {
    id: 'concept:greencrate-value-types',
    type: 'concept',
    keys: [
      'value type',
      'value types',
      'new subscriptions',
      'net sales',
      'first reorder',
      'grossadds',
      'gross adds',
      'netsales',
      'topups',
      'top-up',
      'reorder',
      'subscription funnel',
      'funnel',
      'retention',
      'sign-ups',
      'acquisition',
    ],
    version: '0.1-draft',
    title:
      'GreenCrate value types — the subscription funnel (New Subscriptions, Net Sales, First Reorder)',
    summary:
      'The Sales Funnel and Sales (Actual) KPIs (both on Review KPIs) carry three funnel value types — New Subscriptions (grossadds), Net Sales (netsales), First Reorder (topups) — co-plotted via the Value Type filter.',
    body: `GreenCrate's subscription funnel decomposes into three value types, selectable through the multi-select "Value Type" filter on the Sales Funnel and Sales (Actual) KPIs (both on Review KPIs). Raw token → GreenCrate label → meaning:

- grossadds → New Subscriptions — new sign-ups acquired (top of the funnel; pure acquisition volume).
- netsales → Net Sales — net paying subscriptions after cancellations (the volume that actually converts).
- topups → First Reorder — a new subscriber's first repeat order (the earliest retention signal: did they come back).

Read together, these three tell a volume-versus-retention story: a channel can be heavy on New Subscriptions yet light on First Reorder, meaning it buys sign-ups that do not stick.

How to select them: co-plot them as separate series with set_value_types(["grossadds","netsales","topups"]) — pass the RAW tokens; the UI shows the labels New Subscriptions / Net Sales / First Reorder. The Value Type filter is distinct from the KPI cascade's value-type axis (set_kpi's third argument).

Where they exist: the funnel value types live on two KPIs, both on Review KPIs. Sales Funnel — the driverless, model-attributed funnel root (case 1b) — carries them per medium: pick a stage with the Value Type filter and group by Medium Level 1 for the split; the per-medium values are model-attributed and do not sum to the actual funnel totals (baseline + noise gap). Sales (Actual) carries the same three value types as brand-level actual totals (no medium split). Media-Driven Sales (the model_success_kpi KPI on Contribution) offers only € and Units, not the funnel — see the media-driven-sales vocabulary unit.`,
    seeAlso: [
      'concept:greencrate-media-driven-sales',
      'finding:f5-sea-volume-not-retention',
      'page:ReviewKpis',
    ],
  },
  {
    id: 'concept:greencrate-media-driven-sales',
    type: 'concept',
    keys: [
      'media-driven sales',
      'media driven sales',
      'net spend',
      'nsp',
      'sales roi',
      'sales cpo',
      'opc',
      'mediashare',
      'npv',
      'units',
      'euro',
      'ratio metric',
      'metrics',
    ],
    version: '0.1-draft',
    title: 'GreenCrate KPI vocabulary — Media-Driven Sales and the ratio metrics',
    summary:
      'Media-Driven Sales shows as € and Units; the ratio metrics are Sales RoI (NPV), Sales CpO, Sales OpC, Sales Mediashare, all driven by Net Spend.',
    body: `The KPI names a GreenCrate user sees, and how they map to the v3 encoding:

- Media-Driven Sales (€) / Media-Driven Sales (Units) — the model's media-attributed sales, in euros or subscription units (encoded sales__media__EUR / sales__media__units). This is the decomposition base on Contribution. Its Value Type control offers only € and Units — NOT the subscription funnel.
- Net Spend — media investment, in euros (encoded nsp). Lead KPI on Marketing Invests and the denominator/driver of every ratio metric.
- Ratio metrics (lead on Campaign Performance, Dynamics, Multi Deep Dive):
  - Sales RoI (NPV) — media-driven sales value per euro of net spend (encoded sales_roi__nsp__npv); higher is better.
  - Sales CpO — net spend per order, i.e. acquisition cost; lower is better.
  - Sales OpC — the orders-per-cost companion to CpO.
  - Sales Mediashare — media's share of sales.

Steer the cascade with set_kpi(root, driver, value_type), e.g. root=sales_roi, driver=nsp, value_type=npv. To read the subscription funnel by value type instead, see the value-types unit.`,
    seeAlso: ['kpi:sales_roi', 'kpi:nsp', 'concept:npv', 'concept:cpo'],
  },

  // ─── Dimension axes ─────────────────────────────────────────────────────────
  {
    id: 'dim:greencrate-medium-level-1',
    type: 'dim',
    keys: [
      'medium level 1',
      'media channel',
      'media mix',
      'tv',
      'sea',
      'ooh',
      'display',
      'social',
      'radio',
      'onlinevideo',
      'online video',
      'print',
      'cinema',
      'group by medium',
      'channel',
    ],
    version: '0.1-draft',
    title: 'Medium Level 1 — GreenCrate’s media channels',
    summary:
      'The nine-medium media mix (TV, SEA, OOH, Display, Social, Radio, Onlinevideo, Print, Cinema); the group-by axis behind most media findings.',
    body: `Medium Level 1 is GreenCrate's top-level media-channel axis — group or filter by it to compare per-channel spend and performance. It is the single most navigation-relevant dimension: four of the five findings group by it.

The nine mediums (raw token → label): tv → TV, sea → SEA, ooh → OOH, display → Display, social → Social, radio → Radio, onlinevideo → Onlinevideo, print → Print, cinema → Cinema.

Notable shape: TV carries by far the largest budget share; Onlinevideo is a small line item but an efficiency standout; Print and Cinema are the smallest. Sub-axis Medium Level 2 breaks each medium into submediums (e.g. TV Spot / TV Sponsoring, Search Brand / Search Generic, In-Stream / Out-Stream / Connected TV).

When to use: group by Medium Level 1 on Campaign Performance (RoI and spend), on Contribution (media-driven sales), or on Dynamics (change over time). Steer with set_group_by(["Medium Level 1"]); narrow to specific channels with set_filters({"Medium Level 1": ["TV","SEA"]}).`,
    seeAlso: [
      'finding:f1-onlinevideo-efficiency-star',
      'finding:f2-tv-saturation',
      'page:CampaignPerformance',
      'page:Contribution',
    ],
  },
  {
    id: 'dim:greencrate-topic-level-3',
    type: 'dim',
    keys: [
      'topic level 3',
      'campaign',
      'campaigns',
      'fresh start',
      'summer bbq',
      'christmas feast',
      'veggie launch',
      'wine launch',
      'family deal',
      'express intro',
      'easter menu',
      'named campaign',
      'group by campaign',
      'seasonal',
      'set_campaign',
      'campaign predicate',
      'attribution predicate',
    ],
    version: '0.1-draft',
    title: 'Topic Level 3 — GreenCrate’s named campaigns (also the campaign attribution predicate)',
    summary:
      "GreenCrate's named-campaign axis — group by it for spend breakdowns; selecting a saved Timeframe-control campaign resolves per fact at query time: the exact Campaign dim wherever a fact carries it (on this dataset that includes the model facts behind the ratio metrics, not only spend), its attribution predicate otherwise.",
    body: `Topic Level 3 is the named-campaign axis: each value is a specific marketing campaign. It sits on several facts, but whether grouping or filtering by it gives a meaningful breakdown depends on which KPI you're touching — see the note below before steering by it directly.

Key values (label): Fresh Start (the January relaunch push), Summer BBQ (the mid-year seasonal), Christmas Feast, Easter Menu, Veggie Launch, Wine Launch, Family Deal, Express Intro, plus Always-On, Winter Warmers, Harvest Menu, and Menu Rotation — twelve in total.

Parent axes: Topic Level 2 = campaign category (Seasonal, Launch, Performance, Brand); Topic Level 1 = Campaigns (the single root). Group by Topic Level 2 for a category-level roll-up, Topic Level 3 for individual campaigns.

Selecting a saved campaign (from the Timeframe control's Campaigns group, or steering with set_campaign) writes ONLY its date window and its identity — each fact then resolves its own filter from that identity at query time. Wherever a fact carries the exact Campaign dim, selection filters via that dim — one Campaign chip, the campaign id, NOT this Topic Level 3 axis. That covers the spend facts (Net Spend on Marketing Invests, and the driver arm of any ratio metric) and, on this dataset, the model facts behind the ratio metrics too — so a ratio resolves exact per campaign when BOTH of its component facts carry Campaign. Where a fact does not carry it, selection falls back to the campaign's attribution predicate: Topic Level 3 (and, where populated, Topic Level 4 / Brand Casting Layer Level 1) filtered to that campaign's values, visible as ordinary editable filter chips — genuinely narrowing the rows wherever the fact carries those dims. Where a fact carries neither, the app prunes the inapplicable filter automatically and shows a dismissible notice — the view falls back to window-only there, never a silent wrong answer. Full mechanics and honest limits: the campaign-selection boundary unit.

The first-class "Campaign" dim (the campaign id itself, distinct from this Topic Level 3 label axis) is also a group-by/filter axis in its own right on every fact that carries it — the spend facts (Net Spend on Marketing Invests, and the driver arm of any ratio metric), plus, on this dataset, the model facts behind the ratio metrics — surfaced automatically wherever get_controls exposes it, no special steer needed; it is the same dim campaign selection resolves to on those facts.

When to use: group by Topic Level 3 for spend analysis — Net Spend on Marketing Invests carries Topic Level 3 cleanly, so a per-campaign spend breakdown there is meaningful. Steer with set_group_by(["Topic Level 3"]). To select one campaign's window + identity directly, use set_campaign(campaign) with its id or label (see the campaign-selection boundary unit for the honest limits and the campaign-comparison recipe).

Note: a ratio like Sales RoI resolves exact per campaign only when BOTH of its component facts carry the exact Campaign dim. On this dataset they do (the model facts were extended with Campaign), so Sales RoI partitions cleanly by campaign here; on a dataset where a component fact lacks Campaign, that ratio falls back to the attribution predicate (or window-only) on both sides and the app's pruning shows the notice rather than a silent wrong answer.`,
    seeAlso: [
      'finding:f4-campaign-split',
      'concept:greencrate-campaign-decomposition-boundary',
      'page:CampaignPerformance',
    ],
  },
  {
    id: 'dim:greencrate-kpi-level-1',
    type: 'dim',
    keys: [
      'kpi level 1',
      'kpi level 2',
      'product',
      'product line',
      'box',
      'classic box',
      'veggie box',
      'family box',
      'express box',
      'wine crate',
      'breakfast box',
      'subscription',
      'one-off',
    ],
    version: '0.1-draft',
    title: 'KPI Level 1 / Level 2 — GreenCrate’s box product lines',
    summary:
      'The product axis: six box lines (Classic, Veggie, Family, Express, Wine Crate, Breakfast); Level 2 splits Subscription vs One-Off.',
    body: `KPI Level 1 is GreenCrate's product axis — the box product lines the brand sells. Values (label): Classic Box, Veggie Box, Family Box, Express Box, Wine Crate, Breakfast Box.

KPI Level 2 is the billing mode: Subscription vs One-Off — recurring box subscriptions versus one-off purchases.

When to use: group or filter by KPI Level 1 to compare product-line performance (which box drives sales), by KPI Level 2 to split recurring from one-off. These axes live on the sales facts (e.g. actual sales on Review KPIs, model-attributed sales on Contribution). Steer with set_group_by / set_filters.`,
    seeAlso: ['kpi:sales', 'page:ReviewKpis'],
  },
  {
    id: 'dim:greencrate-channel-level-1',
    type: 'dim',
    keys: [
      'channel level 1',
      'channel level 2',
      'purchase channel',
      'online shop',
      'app',
      'referral',
      'partner retail',
      'direct',
      'promo',
      'sales channel',
    ],
    version: '0.1-draft',
    title: 'Channel Level 1 / Level 2 — GreenCrate’s purchase channels',
    summary:
      'Where the order was placed: Online Shop, App, Referral, Partner Retail; Level 2 splits Direct vs Promo.',
    body: `Channel Level 1 is the purchase-channel axis — where a GreenCrate order is placed. Values (label): Online Shop, App, Referral, Partner Retail.

Channel Level 2 is the purchase path: Direct vs Promo (an ordinary purchase versus one on a promotion).

When to use: group or filter by Channel Level 1 to see how sales split across purchase channels, by Channel Level 2 to isolate promo-driven orders. Steer with set_group_by(["Channel Level 1"]) / set_filters.`,
    seeAlso: ['page:Contribution', 'kpi:sales'],
  },
  {
    id: 'dim:greencrate-driven-by-group',
    type: 'dim',
    keys: [
      'driven by group',
      'media',
      'non-media',
      'baseline',
      'model',
      'fitted',
      'actual',
      'yhat',
      'driver groups',
      'decomposition',
    ],
    version: '0.1-draft',
    title: 'Driven by Group and Model — structural decomposition axes',
    summary:
      'Driven by Group splits Media vs Non-Media (baseline); Model splits Fitted (yhat) vs Actual. Both are structural MMM axes.',
    body: `Two structural axes GreenCrate shares with every MMM tenant:

- Driven by Group — Media vs Non-Media, the media-versus-baseline decomposition. The Contribution page's "Driver Groups" view groups by this axis to show how much of sales media drives versus the organic baseline.
- Model — Fitted (raw token yhat, the model's estimate) vs Actual (the observed value). On the Contribution Driver Groups view, Fitted is the structural default when no Model filter is set.

When to use: switch the Contribution view to Driver Groups for the media-vs-baseline split; filter Model to compare Fitted against Actual. Steer with set_group_by(["Driven by Group"]) and set_filters({"Model": ["Fitted"]}).`,
    seeAlso: ['page:Contribution', 'concept:greencrate-media-driven-sales'],
  },

  // ─── Finding playbooks ────────────────────────────────────────────────────
  // Each is a steerable playbook: WHERE (page) + HOW (exact controls) + WHAT to
  // expect qualitatively + a query_kpi closer. No figures — see file header.
  {
    id: 'finding:f1-onlinevideo-efficiency-star',
    type: 'finding',
    keys: [
      'onlinevideo',
      'online video',
      'efficiency star',
      'hidden efficiency',
      'best roi',
      'top roi',
      'sales roi',
      'small budget',
      'underrated channel',
      'campaign performance',
      'medium level 1',
    ],
    version: '0.1-draft',
    title: 'Finding — Onlinevideo is the efficiency star (small spend, top Sales RoI)',
    summary:
      'On Campaign Performance, grouped by Medium Level 1 over the last calendar year, Onlinevideo posts the top Sales RoI despite one of the smallest spend shares.',
    body: `Where: Campaign Performance.

How to reproduce:
- KPI = Sales RoI (NPV) — encoded sales_roi__nsp__npv (set_kpi with root=sales_roi, driver=nsp, value_type=npv).
- Group by = Medium Level 1 (set_group_by(["Medium Level 1"])).
- Timeframe = Last CY (the last complete calendar year). It is a named frame in the Timeframe control (pick "Last CY", or the fixed-year frame), not one of set_timeframe's presets — steer it by opening the Timeframe picker or setting the calendar-year custom window.

What to expect: Onlinevideo ranks at or near the top of the Sales RoI bar chart while sitting near the bottom of the Net Spend companion chart — a small line item that converts spend more efficiently than the big-budget channels. This is the classic "hidden efficiency" pitch: a channel a client might cut for being small is actually one of their best levers. TV, the largest-spend channel, sits low on RoI by contrast.

Fetch the current figures with query_kpi before quoting any RoI or spend value — never state one from memory or from the dossier.

This is the live-verified reference finding — it reproduced exactly in the smoke test.`,
    seeAlso: ['page:CampaignPerformance', 'kpi:sales_roi', 'dim:greencrate-medium-level-1'],
  },
  {
    id: 'finding:f2-tv-saturation',
    type: 'finding',
    keys: [
      'tv',
      'saturation',
      'half-saturation',
      'diminishing returns',
      'media mix reallocation',
      'reallocation',
      'net spend',
      'media-driven sales',
      'overspend',
      'campaign performance',
      'medium level 1',
    ],
    version: '0.1-draft',
    title: 'Finding — TV saturates fast (heavy spend, capped upside)',
    summary:
      'TV carries the largest media budget but its response curve bends early — its heaviest-spend months convert less efficiently than its lightest, arguing for spreading TV rather than adding euros.',
    body: `Where: Campaign Performance.

How to reproduce:
- Compare Net Spend against Media-Driven Sales across the mediums — group by = Medium Level 1 (set_group_by(["Medium Level 1"])), timeframe = Last CY (a named frame in the Timeframe control, not a set_timeframe preset).
- The Sales RoI (NPV) view (sales_roi__nsp__npv) already plots the Net Spend companion chart alongside RoI, so TV's position shows there too.

What to expect: TV tops the Net Spend chart but lands low on Sales RoI. Its response curve bends past a fairly low half-saturation point, so extra euros in an already-heavy TV month buy much less incremental sales than the same euros in a lighter month. The takeaway is a reallocation conversation: more evenly-spread TV weeks, not more TV euros per week.

Fetch the per-medium (and, for the saturation story, per-month) figures via query_kpi — never quote a saturation ratio from memory.`,
    seeAlso: [
      'page:CampaignPerformance',
      'kpi:nsp',
      'concept:greencrate-media-driven-sales',
      'dim:greencrate-medium-level-1',
    ],
  },
  {
    id: 'finding:f3-print-decline',
    type: 'finding',
    keys: [
      'print',
      'effectiveness decline',
      'fading',
      'wear-out',
      'year over year',
      'creative refresh',
      'media-driven sales',
      'contribution',
      'units',
      'medium level 1',
    ],
    version: '0.1-draft',
    title: 'Finding — Print effectiveness fades year over year',
    summary:
      'Over the model window Print’s media-driven sales response erodes each year independent of spend — a prompt for a creative/targeting-refresh conversation.',
    body: `Where: Contribution.

How to reproduce:
- KPI = Media-Driven Sales (Units) — the model_success_kpi units KPI (encoded sales__media__units).
- Group by = Medium Level 1 (set_group_by(["Medium Level 1"])).
- Timeframe = Last 3 Years — a named rolling frame in the Timeframe control (not a set_timeframe preset).

What to expect: across the three years, Print's media-driven contribution shrinks year over year even where its spend stays roughly steady — the same euro buys less response over time. Because the decline tracks calendar time rather than spend level, it's the "when did you last refresh your creative and targeting?" story. The efficiency (RoI) framing of the same decline is on Campaign Performance — select Sales RoI and group by Medium Level 1 to watch Print's return fall over the years.

Fetch the year-by-year figures via query_kpi.`,
    seeAlso: ['page:Contribution', 'dim:greencrate-medium-level-1', 'kpi:sales'],
  },
  {
    id: 'finding:f4-campaign-split',
    type: 'finding',
    keys: [
      'campaign',
      'fresh start',
      'summer bbq',
      'seasonal',
      'outperform',
      'underperform',
      'budget reallocation',
      'topic level 3',
      'sales roi',
      'campaign performance',
    ],
    version: '0.1-draft',
    title: 'Finding — Campaigns aren’t equal: Fresh Start outperforms, Summer BBQ underperforms',
    summary:
      'Comparing campaign windows on Sales RoI shows the split — the Fresh Start window beats the run-rate, the Summer BBQ window trails it.',
    body: `Where: Campaign Performance — via the campaign list in the Timeframe control (or set_campaign).

Important: selecting a campaign sets its saved time window and identity, and each fact resolves its own filter from that identity. A ratio like Sales RoI resolves exact per campaign wherever BOTH of its component facts carry the exact Campaign dim; on this dataset they do (the model facts were extended with Campaign), so grouping or filtering RoI by named campaign partitions sales cleanly here. On a dataset where a component fact lacks Campaign, that side degrades to the attribution predicate or window-only automatically (with a notice) rather than an exact split — there, compare campaign WINDOWS on the run-rate instead, as below.

On this dataset the model facts behind the ratio metrics carry the exact Campaign dim, so media-driven model views (e.g. Sales RoI) partition exactly per campaign — including the concurrent Menu Rotation waves, which share one topic and which only the exact Campaign dim can separate; on a dataset whose model side lacks Campaign, per-campaign attribution of those modelled effects falls back to the attribution predicate.

How to reproduce (steerable path):
- KPI = Sales RoI (NPV) — encoded sales_roi__nsp__npv.
- Select the most recent Fresh Start campaign (set_campaign("Fresh Start") or the Timeframe control's Campaigns group) — note the overall RoI via query_kpi.
- Select the most recent Summer BBQ campaign (set_campaign("Summer BBQ")) and compare the same way.
- To show both on screen at once instead of switching back and forth, Campaign Performance also supports the multi-timeframe overlay: add one arm per campaign in the multi-timeframe picker — each arm resolves its OWN campaign filter, fact-aware (the exact Campaign dim where the fact carries it, the attribution predicate elsewhere; only that arm's scope changes, the rest of the view is shared), so the two campaign bars sit side by side already correctly scoped.

What to expect: the Fresh Start window (the January relaunch push) runs well above the run-rate on Sales RoI, while the Summer BBQ window (the mid-year seasonal) runs well below it — the budget-reallocation split that makes a strong pitch moment.

Fetch each window's figures via query_kpi before quoting any RoI.`,
    seeAlso: [
      'page:CampaignPerformance',
      'dim:greencrate-topic-level-3',
      'concept:greencrate-campaign-decomposition-boundary',
      'kpi:sales_roi',
    ],
  },
  {
    id: 'finding:f5-sea-volume-not-retention',
    type: 'finding',
    keys: [
      'sea',
      'search',
      'volume',
      'retention',
      'loyalty',
      'new subscriptions',
      'net sales',
      'first reorder',
      'gross adds',
      'reorder',
      'value type',
      'review kpis',
      'medium level 1',
    ],
    version: '0.1-draft',
    title: 'Finding — SEA drives volume, not retention',
    summary:
      "SEA is acquisition-heavy and retention-weak; the per-medium subscription funnel is reachable on Review KPIs via the driverless Sales Funnel KPI grouped by Medium Level 1 (model-attributed split), and mirrored on the Home dashboard's F5 tile.",
    body: `Where: Review KPIs — the driverless "Sales Funnel" KPI, grouped by Medium Level 1.

How to reproduce:
- KPI = Sales Funnel — select it as a bare root in the KPI cascade (no driver). Its "Value Type" filter carries the three funnel stages New Subscriptions, Net Sales, First Reorder (set_value_types(["grossadds","netsales","topups"]) to co-plot them, or pass one raw token for a single stage).
- Group by = Medium Level 1 (set_group_by(["Medium Level 1"])) — this produces the per-medium split.
- Timeframe = Last CY.

Curated view: the "GreenCrate — Key Findings" dashboard on Home, tile F5, plots the same per-medium funnel (Last CY monthly) as a ready-made pitch visual.

The per-medium values are model-attributed and do not sum to the actual funnel totals (baseline + noise gap); say so when citing them. Expect SEA to lead on New Subscriptions while contributing little to Net Sales and First Reorder, in contrast to the broader mediums.

Fetch the per-medium funnel figures with query_kpi (kpi="sales_funnel", page="ReviewKpis", optional value_types for a single stage) before quoting — quote per-medium funnel figures ONLY from query_kpi results, labelled model-attributed. The brand-level funnel totals come from Sales (Actual) on the same page.`,
    seeAlso: ['page:ReviewKpis', 'concept:greencrate-value-types', 'dim:greencrate-medium-level-1'],
  },

  // ─── Capability boundaries (Advisor v2, D1 — steer around, explicitly) ─────
  {
    id: 'concept:greencrate-campaign-decomposition-boundary',
    type: 'concept',
    keys: [
      'per-campaign',
      'campaign roi',
      'campaign performance',
      'campaign contribution',
      'topic level',
      'campaign breakdown',
      'campaign decomposition',
      'by campaign',
      'campaign predicate',
      'attribution predicate',
      'set_campaign',
      'campaign limits',
      'honest limits',
    ],
    version: '0.1-draft',
    title: 'Campaign selection — identity-only, fact-aware filtering, and its three honest limits',
    summary:
      'Selecting a saved campaign writes only its time window and its identity; each fact then resolves its OWN filter — the exact Campaign dim wherever the fact carries it, an attribution predicate elsewhere, or window-only where neither applies — with three limits that stay true regardless.',
    body: `Selecting a saved campaign (via the Timeframe control's Campaigns group, or set_campaign) writes ONLY its saved date window and its campaign identity — no attribution values are written to the URL. Every fact then resolves its own filter from that identity at query time:

Any fact that carries the exact Campaign dim filters exactly by it (the campaign id, not a Topic Level 3 / BCL tuple), so it partitions cleanly by campaign even when two campaigns share the same attribution values and overlapping windows. This holds on the spend facts (Net Spend on Marketing Invests, and the driver arm of any ratio metric) and — on this dataset — on the model facts behind the ratio metrics as well; a ratio partitions exactly per campaign wherever BOTH of its component facts carry the Campaign dim.

On every other fact, selection falls back to the campaign's attribution predicate (Topic Level 3, plus Topic Level 4 / Brand Casting Layer Level 1 where the campaign has them), applied as ordinary editable filter chips. Where a fact doesn't carry even those dims, the app prunes the predicate automatically and shows a visible notice; the view degrades to window-only rather than pretending a partition exists.

Three honest limits hold regardless of the resolution mechanics, and the corpus must never imply otherwise:

1. Concurrent campaigns that share the same attribution tuple within overlapping windows remain inseparable at the row level on any fact that falls back to the attribution predicate — the predicate cannot split them apart (the Menu Rotation waves are exactly this shape: one shared topic across an overlapping frame). A fact that carries the exact Campaign dim IS the exception: it DOES split concurrent same-tuple campaigns apart; on this dataset that covers the spend facts and the model facts behind the ratio metrics, so those surfaces separate the Menu Rotation waves where a plain topic filter cannot.
2. Raw sales actuals (the Sales KPI's actual value type, and the actuals side of any ratio metric) are never campaign-scoped — a selected campaign narrows only their DATE window, never their rows; the app's visible notice covers this case.
3. Per-campaign figures for a modelled series are exact only where its fact carries the exact Campaign dim; a series on a fact that lacks it is not campaign-classified, so grouping or filtering it by campaign falls back to the attribution predicate (or window-only) rather than an exact per-campaign split. On this dataset the model facts behind the ratio metrics DO carry Campaign, so Sales RoI and the other ratios resolve exact per campaign here; on a dataset whose model side has not been extended with Campaign yet, those series stay attribution-only until it is.

On this dataset the model facts behind the ratio metrics carry the exact Campaign dim, so media-driven model views (e.g. Sales RoI) partition exactly per campaign here — including telling the Menu Rotation waves apart, which a topic filter alone cannot; on a dataset whose model side has not yet been extended with Campaign, those views fall back to the attribution predicate.

Never state a campaign-scoped sales-actual figure as if the resolved filter produced it — it does not; and for a modelled series, trust a per-campaign figure only where its fact carries the exact Campaign dim (the app's applicability pruning shows a notice where it does not). Only query_kpi results after that pruning are trustworthy here.

Steer-around recipe (comparing campaigns on RoI or any model KPI):

- Select each campaign in turn with set_campaign(campaign) — this writes the window and campaign identity atomically; each fact resolves its own filter automatically (exact Campaign dim wherever the fact carries it — on this dataset the spend facts AND the model facts behind the ratio metrics — attribution predicate elsewhere, window-only where neither applies), so you never need to reason about which dims a fact carries.
- Call query_kpi once per campaign selection (same KPI, filters, grouping; only the campaign changes) and compare the results side by side, naming each campaign.
- To show two or more campaigns on screen at once instead of switching back and forth, use the multi-timeframe overlay on pages that carry it (Campaign Performance, Dynamics, Multi Deep Dive) — each arm can be set to its own campaign and resolves its own filter independently.

Before answering, check the Dataset Overview's campaign list: each entry now carries its id and a compact attribution-tuple summary (the fallback predicate on non-carrying facts, not necessarily what a given walk applied), so you can see what a given campaign's fallback would touch before steering or comparing.`,
    seeAlso: [
      'concept:data-boundaries',
      'concept:greencrate-brand',
      'dim:greencrate-topic-level-3',
      'finding:f4-campaign-split',
    ],
  },
  {
    id: 'concept:greencrate-funnel-boundary',
    type: 'concept',
    keys: [
      'funnel by medium',
      'funnel by channel',
      'which medium drives reorders',
      'retention by channel',
      'funnel split',
      'per-medium funnel',
      'funnel decomposition',
      'funnel by medium available',
      'sales funnel',
      'funnel on review kpis',
    ],
    version: '0.1-draft',
    title:
      'Funnel × medium — model-attributed decomposition, first-class on Review KPIs (Sales Funnel KPI)',
    summary:
      'The per-medium subscription funnel is first-class on Review KPIs — the driverless Sales Funnel KPI, grouped by Medium Level 1 — and query_kpi reaches it; the per-medium values are model-attributed, distinct from the brand-level actual funnel totals.',
    body: `The subscription funnel — New Subscriptions, Net Sales, First Reorder — is first-class PER MEDIUM on Review KPIs (case 1b). Select the driverless "Sales Funnel" KPI as a bare root in the KPI cascade (no driver), pick a stage with its "Value Type" filter (New Subscriptions / Net Sales / First Reorder), and group by "Medium Level 1" for the per-medium split. query_kpi reaches it with kpi="sales_funnel", page="ReviewKpis", and an optional value_types array of raw tokens to isolate a single stage. The "GreenCrate — Key Findings" dashboard's F5 tile on Home plots the same per-medium funnel as a curated pitch visual.

Two streams, one honest distinction:
- Model-attributed funnel per medium — "the model attributes this many New Subscriptions to this medium". These are the Sales Funnel KPI's rows; the per-medium values are model-attributed and do not sum to the actual funnel totals (baseline + noise gap), exactly as media-attributed sales relate to actual sales.
- Actual funnel totals — brand-level outcomes, no medium dims, carried by the Sales (Actual) KPI on the same page. Use these for the overall funnel-narrowing story; fetch them live.

Anti-fabrication rule: quote per-medium funnel figures ONLY from query_kpi results, and label them model-attributed — never invent them. Media-attributed Sales volume or RoI grouped by "Medium Level 1", and the brand-level funnel totals from Sales (Actual), are also fetchable live; label each clearly for what it is.`,
    seeAlso: [
      'concept:data-boundaries',
      'concept:greencrate-value-types',
      'finding:f5-sea-volume-not-retention',
      'page:ReviewKpis',
    ],
  },
]

/** Welcome-block starter questions. NO figures — numbers must come from live query_kpi. */
export const GREENCRATE_EXAMPLE_QUESTIONS: string[] = [
  'Which medium delivered the best Sales RoI last calendar year?',
  'How are New Subscriptions developing this year?',
  'Where does our media budget go, and what does it contribute?',
  'Compare our two most recent campaign windows on RoI.',
]

export const GREENCRATE_OVERLAY: TenantOverlay = {
  units: GREENCRATE_UNITS,
  exampleQuestions: GREENCRATE_EXAMPLE_QUESTIONS,
}
